import { Box } from '@mui/material';
import { useCompanyRelativePath } from 'ogp/hooks';
import { routes } from 'ogp/Routes';
import { flexPoolAlerts as alerts, useAlert } from 'shared/components/alerts';
import { Header, LwButton, Page, Toolbox, useFormButtons } from 'redesign';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CreateFlexPoolExternalPayload } from 'ogp/services/flex-pool-service.types';
import { useGetServiceAgreementsForCompany } from 'ogp/hooks/queryHooks/service-agreements/use-get-service-agreements-for-company';
import { RenderUnsettledUI } from 'shared/components';
import { useCreateFlexPoolExternal } from './hooks/use-create-flex-pool-external';
import { FlexPoolExternalForm } from './flex-pool-external-form';
import { FlexPoolExternalFormData } from './flex-pool-external.types';

export const FlexPoolExternalCreate = () => {
  const { t } = useTranslation(['ogp-screen-specific', 'web-common']);
  const generatePath = useCompanyRelativePath();
  const { alertSuccess, alertError } = useAlert();
  const formMethods = useForm<FlexPoolExternalFormData>({
    defaultValues: { name: '', description: '', serviceAgreements: [] },
  });
  const navigate = useNavigate();
  const mutation = useCreateFlexPoolExternal({
    onSuccess: () => {
      alertSuccess(alerts.success.created);
      navigate(generatePath(routes.FlexPools));
    },
    onError: (e) => alertError(e),
  });
  const { submitButtonProps, cancelButtonProps, returnTo } = useFormButtons(mutation.status);
  const serviceAgreementsQuery = useGetServiceAgreementsForCompany({
    isLender: true,
    oppositeParty: 'COMPANY',
  });

  if (serviceAgreementsQuery.status !== 'success') {
    return <RenderUnsettledUI data={serviceAgreementsQuery} />;
  }

  const handleSubmit = () => {
    formMethods.handleSubmit((data) => {
      const payload: CreateFlexPoolExternalPayload = {
        name: data.name,
        description: data.description,
        serviceAgreementIds: (data.serviceAgreements ?? []).map((s) => s.id),
      };
      mutation.mutate(payload);
    })();
  };

  return (
    <FormProvider {...formMethods}>
      <Page
        header={
          <Header titleText={t('flexpools.external_create.title', { ns: 'ogp-screen-specific' })} />
        }
        toolbox={
          <Toolbox underline>
            <LwButton onClick={handleSubmit} iconColorMode="stroke" {...submitButtonProps}>
              {t('buttons.create', { ns: 'web-common' })}
            </LwButton>
            <LwButton
              onClick={returnTo(routes.FlexPools)}
              iconColorMode="fill"
              {...cancelButtonProps}
            >
              {t('buttons.cancel', { ns: 'web-common' })}
            </LwButton>
          </Toolbox>
        }
      >
        <Box width={'100%'}>
          <FlexPoolExternalForm serviceAgreements={serviceAgreementsQuery.data} />
        </Box>
      </Page>
    </FormProvider>
  );
};
